import { type Static, Type as t } from "@sinclair/typebox";
import { GalleryCard } from "./gallery";
import {
  CursorQuery,
  closed,
  Empty,
  GidParam,
  IsoDateTime,
  LimitParam,
  nullable,
  pageOf,
  Uuid,
} from "./primitives";
import { route, withIds } from "./route";

export const BookmarkVisibility = t.Union([t.Literal("private"), t.Literal("public")]);
export const BookmarkScope = t.Union([t.Literal("all"), t.Literal("mine"), t.Literal("public")]);
export const BookmarkInput = closed({
  name: t.String({ minLength: 1, maxLength: 60, pattern: "\\S" }),
  description: t.Optional(t.String({ maxLength: 300 })),
  visibility: t.Optional(BookmarkVisibility),
});
export const Bookmark = closed({
  id: Uuid,
  name: t.String(),
  description: t.String(),
  visibility: BookmarkVisibility,
  isDefault: t.Boolean(),
  isOwner: t.Boolean(),
  isSaved: t.Boolean(),
  ownerName: t.String(),
  itemCount: t.Integer({ minimum: 0 }),
  updatedAt: IsoDateTime,
  previews: t.Array(GalleryCard, { maxItems: 3 }),
});
export type BookmarkType = Static<typeof Bookmark>;
export const GalleryIds = closed({
  gids: t.Array(t.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }), {
    maxItems: 100,
    uniqueItems: true,
  }),
});
export const BookmarkMembership = closed({
  gid: t.Integer({ minimum: 1 }),
  bookmarkIds: t.Array(Uuid),
  isFavorite: t.Boolean(),
});
export const MembershipChanges = closed({
  addIds: t.Array(Uuid, { maxItems: 100, uniqueItems: true }),
  removeIds: t.Array(Uuid, { maxItems: 100, uniqueItems: true }),
});
const Id = t.Object({ id: Uuid });
export const bookmarkRoutes = withIds({
  "personal.listBookmarks": route({
    module: "personal",
    method: "GET",
    path: "/api/bookmarks",
    access: "user",
    summary: "내 북마크와 회원 공개 북마크",
    query: t.Object({
      scope: t.Optional(BookmarkScope),
      cursor: t.Optional(t.String({ minLength: 1, maxLength: 512 })),
      limit: t.Optional(LimitParam),
    }),
    responses: { 200: pageOf(Bookmark) },
    errors: [],
  }),
  "personal.createBookmark": route({
    module: "personal",
    method: "POST",
    path: "/api/bookmarks",
    access: "user",
    summary: "프라이빗 북마크 만들기",
    body: BookmarkInput,
    responses: { 201: Bookmark },
    errors: [],
  }),
  "personal.getBookmark": route({
    module: "personal",
    method: "GET",
    path: "/api/bookmarks/:id",
    access: "user",
    summary: "접근 가능한 북마크",
    params: Id,
    responses: { 200: Bookmark },
    errors: ["NOT_FOUND"],
  }),
  "personal.updateBookmark": route({
    module: "personal",
    method: "PATCH",
    path: "/api/bookmarks/:id",
    access: "user",
    summary: "내 북마크 편집",
    params: Id,
    body: BookmarkInput,
    responses: { 200: Bookmark },
    errors: ["NOT_FOUND"],
  }),
  "personal.deleteBookmark": route({
    module: "personal",
    method: "DELETE",
    path: "/api/bookmarks/:id",
    access: "user",
    summary: "내 북마크 삭제(기본 제외)",
    params: Id,
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),
  "personal.bookmarkItems": route({
    module: "personal",
    method: "GET",
    path: "/api/bookmarks/:id/items",
    access: "user",
    summary: "북마크 작품",
    params: Id,
    query: CursorQuery,
    responses: { 200: pageOf(GalleryCard) },
    errors: ["NOT_FOUND"],
  }),
  "personal.saveBookmark": route({
    module: "personal",
    method: "PUT",
    path: "/api/bookmarks/:id/saved",
    access: "user",
    summary: "타인 공개 북마크 참조 저장",
    params: Id,
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),
  "personal.unsaveBookmark": route({
    module: "personal",
    method: "DELETE",
    path: "/api/bookmarks/:id/saved",
    access: "user",
    summary: "북마크 참조 해제",
    params: Id,
    responses: { 204: Empty },
    errors: [],
  }),
  "personal.bookmarkMemberships": route({
    module: "personal",
    method: "POST",
    path: "/api/bookmark-memberships",
    access: "user",
    summary: "현재 작품의 내 북마크 소속",
    body: GalleryIds,
    responses: { 200: closed({ items: t.Array(BookmarkMembership) }) },
    errors: [],
  }),
  "personal.changeBookmarkMemberships": route({
    module: "personal",
    method: "PATCH",
    path: "/api/bookmark-memberships/:gid",
    access: "user",
    summary: "내 북마크 소속 변경분 원자 적용",
    params: t.Object({ gid: GidParam }),
    body: MembershipChanges,
    responses: { 204: Empty },
    errors: ["NOT_FOUND"],
  }),
});
export const catalogExtraRoutes = withIds({
  "catalog.cards": route({
    module: "catalog",
    method: "POST",
    path: "/api/galleries/cards",
    access: "user",
    summary: "정책 적용 카드 배치",
    body: GalleryIds,
    responses: { 200: closed({ items: t.Array(GalleryCard) }) },
    errors: [],
  }),
  "catalog.syncStatus": route({
    module: "catalog",
    method: "GET",
    path: "/api/catalog/sync-status",
    access: "user",
    summary: "목록 확인 시각과 상세정보 수집 중단 상태",
    responses: {
      200: closed({ lastSuccessAt: nullable(IsoDateTime), metadataPausedAt: nullable(IsoDateTime) }),
    },
    errors: [],
  }),
});
